import type {
  ChangePasswordDto,
  ForgotPasswordDto,
  LoginDto,
  PhoneDto,
  ResendCodeDto,
  SetPasswordDto,
} from '@/api';
import helper from '@/constants/helper';
import { languageToValue } from '@/constants/languageToValue';
import { QK } from '@api/queryKeys';
import routes from '@constants/routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { disconnectSocket } from '@utils/socket';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { UpdateUserPayload, userApi } from './user';

// ========================
// Query Hooks
// ========================

export const useGetUser = () => {
  return useQuery({
    queryKey: QK.user(),
    queryFn: () => userApi.getUser(),
  });
};

export const useGetAllUsers = () => {
  return useQuery({
    queryKey: QK.getAllUsers(),
    queryFn: () => userApi.getAllUsers(),
  });
};

// ========================
// User Mutation Hooks
// ========================

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<UpdateUserPayload>) => userApi.updateUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK.user() });
    },
  });
};

export const useResendCode = () => {
  return useMutation({
    mutationFn: (data: ResendCodeDto) => userApi.resendCode(data),
  });
};

export const usePhone = () => {
  return useMutation({
    mutationFn: (data: PhoneDto) => userApi.phone(data),
  });
};

export const useVerifyUserIdentity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => userApi.verifyUserIdentity(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK.user() });
    },
  });
};

export const useConfirmIndependence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId }: { auditId: number }) => userApi.confirmIndependence({ auditId }),
    onSuccess: (_, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: QK.user() });
      queryClient.invalidateQueries({ queryKey: QK.audit(String(auditId)) });
      queryClient.invalidateQueries({ queryKey: QK.audits() });
    },
  });
};

export const useRejectIndependence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId }: { auditId: number }) => userApi.rejectIndependence({ auditId }),
    onSuccess: (_, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: QK.user() });
      queryClient.invalidateQueries({ queryKey: QK.audit(String(auditId)) });
      queryClient.invalidateQueries({ queryKey: QK.audits() });
    },
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: (data: ChangePasswordDto) => userApi.changePassword(data),
  });
};

export const useSetPassword = () => {
  return useMutation({
    mutationFn: ({ data, token }: { data: SetPasswordDto; token: string }) =>
      userApi.setPassword(data, token),
  });
};

// Sends the reset 2FA code to the user's registered (pinned) phone.
export const useSendResetSms = () => {
  return useMutation({
    mutationFn: (token: string) => userApi.resetSendSms(token),
  });
};

// Verifies the reset SMS code (no consume) so the UI can gate the password step.
export const useVerifyResetSms = () => {
  return useMutation({
    mutationFn: ({ code, token }: { code: string; token: string }) =>
      userApi.resetVerifySms({ code }, token),
  });
};

// Commits a new password during reset, gated on the SMS code. The user has
// just passed email + SMS 2FA, so the backend returns a full auth payload —
// persist the session token and warm the user cache exactly like login + 2FA
// (see useVerifySmsMutation) instead of bouncing them back to the login page.
export const useResetPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ code, password, token }: { code: string; password: string; token: string }) =>
      userApi.resetPassword({ code, password }, token),
    onSuccess: async (response) => {
      if (response && response.success) {
        if (response.token) {
          localStorage.setItem(helper.JWT_TOKEN, response.token);
        }

        try {
          const currentUserData = await userApi.getUser();
          queryClient.setQueryData(QK.user(), currentUserData);
        } catch (error) {
          console.error('Failed to fetch user data after password reset:', error);
          queryClient.invalidateQueries({ queryKey: QK.user() });
        }
      }
    },
  });
};

// ========================
// Auth Mutation Hooks
// ========================

export const useLoginMutation = () => {
  const { i18n } = useTranslation();
  const history = useHistory();

  return useMutation({
    mutationFn: (data: LoginDto) => userApi.login(data),
    onSuccess: (response) => {
      // Store token in localStorage
      if (response.token) {
        localStorage.setItem(helper.TEMP_SMS_TOKEN, response.token);
      }

      // Set language if provided in login response
      if (response.language && response.language in languageToValue) {
        const lang = languageToValue[response.language as keyof typeof languageToValue];
        i18n.changeLanguage(lang).then(() => {
          localStorage.setItem('i18nextLng', lang);
        });
      }

      // Handle different response messages
      if (response.message === 'Need to reset password') {
        history.push(routes.auth.forgotPasswordSent);
      } else if (response.message === 'Need to set up password') {
        history.push(`${routes.auth.setPassword}?token=${response.token}`);
      } else if (response.message === 'Need to verify phone') {
        if (response.phone) {
          // Store phone temporarily for verification flow
          localStorage.setItem(helper.TEMP_PHONE, response.phone);
          history.push(routes.auth.phoneVerification);
        } else {
          // Store partial user data for phone setup flow
          localStorage.setItem(helper.TEMP_USER_DATA, JSON.stringify(response));
          history.push(routes.auth.phoneLogin);
        }
      }
    },
  });
};

export const useLogoutMutation = () => {
  const queryClient = useQueryClient();
  const history = useHistory();

  return useMutation({
    mutationFn: () => userApi.logout(),
    onSuccess: () => {
      // Clear localStorage
      disconnectSocket();
      localStorage.removeItem(helper.JWT_TOKEN);

      // Clear React Query cache
      queryClient.removeQueries({ queryKey: QK.user() });
      queryClient.clear();

      // Redirect to login
      history.push(routes.auth.login);
    },
    onError: () => {
      // Even if logout fails on server, clear local data
      disconnectSocket();
      localStorage.removeItem(helper.JWT_TOKEN);
      queryClient.removeQueries({ queryKey: QK.user() });
      queryClient.clear();
      history.push(routes.auth.login);
    },
  });
};

export const useForgotPasswordMutation = () => {
  const history = useHistory();

  return useMutation({
    mutationFn: (data: ForgotPasswordDto) => userApi.forgotPasswordEmail(data),
    onSuccess: (response) => {
      if (response.message === 'Email sent') {
        history.push('/login/email/send');
      }
    },
    onError: (error: unknown) => {
      console.error('Forgot password error:', error);
    },
  });
};

// ========================
// SMS Verification Hooks
// ========================

interface VerifySmsParams {
  code: string;
  token: string;
}

export const useVerifySmsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ code, token }: VerifySmsParams) => userApi.verifySms({ code }, token),
    onSuccess: async (response) => {
      // The response should contain the complete user data
      if (response && response.success) {
        // Store token in localStorage
        if (response.token) {
          localStorage.setItem(helper.JWT_TOKEN, response.token);
        }

        // Fetch and cache updated user data
        try {
          const currentUserData = await userApi.getUser();
          queryClient.setQueryData(QK.user(), currentUserData);
        } catch (error) {
          console.error('Failed to fetch user data after SMS verification:', error);
          // Fallback to invalidating queries if direct fetch fails
          queryClient.invalidateQueries({ queryKey: QK.user() });
        }
      }
    },
  });
};

interface SendSmsParams {
  phone: string;
  token: string;
}

export const useSendSmsMutation = () =>
  useMutation({
    mutationFn: ({ phone, token }: SendSmsParams) => userApi.sendSms({ phone }, token),
  });

/**
 * Alias for useVerifySmsMutation for backward compatibility
 * @deprecated Use useVerifySmsMutation instead
 */
export const useVerifySms = useVerifySmsMutation;

/**
 * Alias for useSendSmsMutation for backward compatibility
 * @deprecated Use useSendSmsMutation instead
 */
export const useSendSms = useSendSmsMutation;

export const useGetNotifications = (
  type: 'general' | 'archive' | 'important' | string = 'important',
) => {
  return useQuery({
    queryKey: QK.notifications(type),
    queryFn: () => userApi.getNotifications({ type }),
  });
};

export const useReadGeneralNotifications = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => userApi.readGeneralNotifications(),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: QK.notifications('general') });
      queryClient.invalidateQueries({ queryKey: QK.user() }); // User object has notification counts
    },
  });
};

export const useReadImportantNotifications = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: number) => userApi.readImportantNotifications(notificationId),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: QK.notifications('important') });
      queryClient.invalidateQueries({ queryKey: QK.user() });
    },
  });
};

export const useUploadSignature = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (signature: any) => userApi.uploadSignature(signature),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK.signature() });
      queryClient.invalidateQueries({ queryKey: QK.user() }); // user might have signature status
    },
  });
};

export const useGetSignature = () => {
  return useQuery({
    queryKey: QK.signature(),
    queryFn: () => userApi.getSignature(),
  });
};

export const useIsUserActive = () => {
  return useMutation({
    mutationFn: (token: string) => userApi.isUserActive(token),
  });
};
